import React from 'react';
import { observer } from 'mobx-react-lite';
import { useAppStateContext } from 'src/AppState/AppState';
import { useCommon } from 'src/AppState/Common';
import { ConfigState } from 'src/AppState/ConfigState/ConfigState';
import { Permission } from 'src/AppState/PermissionGrants/PermissionGrants.types';
import { ArrowIconWrapper, MenuButtonWrapper } from 'src/components/Menu/Menu.style';
import { MenuItemsNavigationAdmin } from 'src/layouts/AdminPage/AdminMenuItems.style';

type AdminRouteType =
    | 'users'
    | 'role-and-permissions'
    | 'country-defaults'
    | 'change-password'
    | 'system-settings'
    | 'pricing-ladders'
    | 'chat-night-mode'
    | 'casino-mobile-version'
    | 'app-update'
    | 'configuration-manager'
    | 'compliance-configuration';

export const AdminMenuItems = observer((): JSX.Element | null => {
    const common = useCommon();
    const { routerBackoffice, permissionGrantsState } = useAppStateContext();
    const {
        config: { configurationManagerUI },
    } = ConfigState.get(common);
    const hasComplianceConfigPermission = permissionGrantsState.has(Permission.complianceComplianceConfig);
    const customerParams = routerBackoffice.getParametersString('global', 'tab') ?? 'users';

    const isTabActive = (label: string): boolean => {
        return customerParams === label;
    };

    // eslint-disable-next-line @typescript-eslint/naming-convention
    interface MenuItems {
        id: AdminRouteType;
        label: string;
        onClick: () => void;
    }

    const configurationManagerUIMenuItems: Array<MenuItems> = configurationManagerUI
        ? [
              {
                  id: 'configuration-manager',
                  label: 'Configuration Manager',
                  onClick: (): void => routerBackoffice.redirectToAdminConfigurationManager(),
              },
          ]
        : [];

    const complianceConfigurationMenuItems: Array<MenuItems> = hasComplianceConfigPermission
        ? [
              {
                  id: 'compliance-configuration',
                  label: 'Compliance Configuration',
                  onClick: (): void => routerBackoffice.redirectToAdminComplianceConfiguration(),
              },
          ]
        : [];

    const menuItems: Array<MenuItems> = [
        {
            id: 'users',
            label: 'Users',
            onClick: (): void => routerBackoffice.redirectToAdminUser(),
        },
        {
            id: 'role-and-permissions',
            label: 'Roles and Permissions',
            onClick: (): void => routerBackoffice.redirectToAdminRoleAndPermission(),
        },
        {
            id: 'country-defaults',
            label: 'Country defaults',
            onClick: (): void => routerBackoffice.redirectToAdminCountryDefaults(),
        },
        {
            id: 'change-password',
            label: 'Change password',
            onClick: (): void => routerBackoffice.redirectToAdminChangePassword(),
        },
        {
            id: 'system-settings',
            label: 'System settings',
            onClick: (): void => routerBackoffice.redirectToAdminSystemSettings(),
        },
        {
            id: 'pricing-ladders',
            label: 'Pricing ladders',
            onClick: (): void => routerBackoffice.redirectToAdminPricingLadders(),
        },
        // {
        //     id: 'related-contingency',
        //     label: 'Related contingency',
        //     onClick: (): void => routerBackoffice.redirectToAdminRelatedContingency(),
        // },
        {
            id: 'chat-night-mode',
            label: 'Chat night mode',
            onClick: (): void => routerBackoffice.redirectToAdminChatNightMode(),
        },
        {
            id: 'casino-mobile-version',
            label: 'App in review state',
            onClick: (): void => routerBackoffice.redirectToAdminCasinoMobileVersion(),
        },
        {
            id: 'app-update',
            label: 'App Update',
            onClick: (): void => routerBackoffice.redirectToAdminAppUpdate(),
        },
        ...complianceConfigurationMenuItems,
        ...configurationManagerUIMenuItems,
    ];

    return (
        <MenuItemsNavigationAdmin>
            {menuItems.map((linkItem) => (
                <MenuButtonWrapper
                    key={linkItem.id}
                    isActive={isTabActive(linkItem.id)}
                    onClick={linkItem.onClick}
                >
                    {linkItem.label}
                    <ArrowIconWrapper
                        isActive={isTabActive(linkItem.id)}
                        position='right'
                    />
                </MenuButtonWrapper>
            ))}
        </MenuItemsNavigationAdmin>
    );
});
